import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useParams } from 'react-router-dom';
import type { EvidenceLabel, ImageLabelsSidecar, PhaseStatus } from '@sr-test-wizard/shared';
import { api } from '../../lib/api';
import { runAssetsApi, type RunImage } from '../../lib/runAssets';
import { useRunStore } from '../../state/runStore';
import { StatusIcon } from '../../components/StatusIcon';
import { StatTile } from '../../components/StatTile';
import { ImagesGalleryDialog } from '../phase3/ImagesGalleryDialog';
import { evidenceLabelsApi } from '../../lib/evidenceLabelsApi';

function PhasePill({ status }: { status: PhaseStatus | undefined }) {
  const s = status ?? 'pending';
  return <span className={`phase-pill ${s}`}>{s.replace('_', ' ')}</span>;
}

/**
 * Phase 4 dashboard panel - Game-Help Screenshots.
 *
 * Drives the existing `/download-images` and `/analyze-images` endpoints
 * (no logic change) but presents the result inline: live thumbnail grid,
 * stat tiles, and the cached vision analysis rendered as Markdown. The
 * full-screen gallery is still available via the existing dialog.
 *
 * NOTE: this panel still lives under `features/phase2/` for git-history
 * reasons; the visible step number changed to 4 when the rail was
 * reordered. See `App.tsx` for the URL <-> folder mapping.
 */
export function ImagesPanel() {
  const { id } = useParams<{ id: string }>();
  const runId = id!;
  const { current, loadRun, setPhase } = useRunStore();
  const [images, setImages] = useState<RunImage[]>([]);
  const [analysisExists, setAnalysisExists] = useState(false);
  const [analysisContent, setAnalysisContent] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [gallery, setGallery] = useState(false);
  const [zoom, setZoom] = useState<RunImage | null>(null);
  const [imageLabels, setImageLabels] = useState<ImageLabelsSidecar | null>(null);
  const [labelDefs, setLabelDefs] = useState<EvidenceLabel[]>([]);

  const loadAssets = useMemo(
    () => async () => {
      const [imgs, ana, labels] = await Promise.all([
        runAssetsApi.listImages(runId),
        runAssetsApi.loadImagesAnalysis(runId),
        runAssetsApi.loadImageLabels(runId).catch(() => null),
      ]);
      setImages(imgs.items);
      setAnalysisExists(ana.exists);
      setAnalysisContent(ana.content);
      setImageLabels(labels);
    },
    [runId],
  );

  useEffect(() => {
    let cancelled = false;
    void evidenceLabelsApi
      .list()
      .then((bundle) => {
        if (!cancelled) setLabelDefs(bundle.entries);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const labelDefByKey = useMemo(() => {
    const m = new Map<string, EvidenceLabel>();
    for (const def of labelDefs) m.set(def.key.toLowerCase(), def);
    return m;
  }, [labelDefs]);

  useEffect(() => {
    void loadAssets().catch(() => undefined);
  }, [loadAssets]);

  const runAction = async (label: string, endpoint: string, phase: 'drive' | 'analyze-images', body?: unknown) => {
    setBusy(label);
    setError(null);
    setPhase(phase, 'in_progress');
    try {
      await api.post(`/api/runs/${runId}/${endpoint}`, body);
      // Job is queued; WS phase events update status. Soft-refresh assets shortly.
      setTimeout(() => {
        void loadRun(runId).then(() => loadAssets());
      }, 2500);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // Register + list locally-dropped screenshots without running any AI. This is
  // the "alternative to Download" path; the separate "Analyze Images" button
  // runs the vision pass afterwards.
  const scanFolder = async () => {
    setBusy('scan');
    setError(null);
    try {
      await api.post(`/api/runs/${runId}/images/scan`);
      await loadRun(runId);
      await loadAssets();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (!current) return null;
  const drivePhase = current.phases?.['drive'];
  const analyzePhase = current.phases?.['analyze-images'];

  return (
    <div className="flex-col">
      <div className="card phase-action-card">
        <div className="row">
          <StatusIcon
            status={drivePhase === 'completed' && analyzePhase === 'completed' ? 'completed' : drivePhase ?? 'pending'}
            size={24}
          />
          <div className="phase-action-text">
            <h2 style={{ margin: 0 }}>Phase 4 - Download &amp; Analyze Images</h2>
            <p className="muted small" style={{ margin: '4px 0 0' }}>
              Locates the TEXEC -&gt; game-rules folder on Google Drive and pulls every screenshot, then
              runs the cached GPT-4o Vision analysis and writes a Markdown summary alongside the images.
            </p>
          </div>
          <span className="spacer" />
          <PhasePill status={drivePhase} />
          <PhasePill status={analyzePhase} />
        </div>
        <div className="row" style={{ marginTop: 12, flexWrap: 'wrap' }}>
          <button
            className="btn btn-primary"
            disabled={busy !== null}
            onClick={() => void runAction('drive', 'download-images', 'drive')}
          >
            {busy === 'drive'
              ? 'Downloading...'
              : drivePhase === 'completed'
                ? 'Re-download Images'
                : 'Download Images'}
          </button>
          <button
            className="btn"
            disabled={busy !== null}
            title="Register screenshots you dropped into the run's images folder on disk (no Drive download, no AI). Then click Analyze Images."
            onClick={() => void scanFolder()}
          >
            {busy === 'scan' ? 'Scanning...' : 'Scan Local Folder'}
          </button>
          <button
            className="btn"
            disabled={busy !== null || (drivePhase !== 'completed' && images.length === 0)}
            onClick={() => void runAction('analyze', 'analyze-images', 'analyze-images')}
          >
            {busy === 'analyze'
              ? 'Analyzing...'
              : analyzePhase === 'completed'
                ? 'Re-analyze Images'
                : 'Analyze Images'}
          </button>
          <button
            className="btn btn-ghost"
            disabled={busy !== null || (drivePhase !== 'completed' && images.length === 0)}
            onClick={() => void runAction('analyze-force', 'analyze-images', 'analyze-images', { force: true })}
          >
            Force Re-analyze
          </button>
          <span className="spacer" />
          <button className="btn" disabled={images.length === 0} onClick={() => setGallery(true)}>
            Open Full Gallery
          </button>
        </div>
        {error && (
          <p style={{ color: 'var(--bad)', marginTop: 8 }}>{error}</p>
        )}
      </div>

      <div className="stat-grid">
        <StatTile label="Screenshots" value={images.length} tone={images.length > 0 ? 'good' : 'default'} />
        <StatTile
          label="Vision Analysis"
          value={analysisExists ? 'Cached' : 'None'}
          tone={analysisExists ? 'good' : 'default'}
        />
        <StatTile
          label="Drive Folder"
          value={drivePhase === 'completed' ? 'Resolved' : drivePhase === 'failed' ? 'Failed' : 'Pending'}
          tone={drivePhase === 'completed' ? 'good' : drivePhase === 'failed' ? 'bad' : 'default'}
          hint={current.images_path ?? undefined}
        />
      </div>

      <div className="card">
        <div className="row">
          <strong>Screenshots</strong>
          <span className="muted small" style={{ marginLeft: 8 }}>
            {images.length} {images.length === 1 ? 'image' : 'images'}
          </span>
          <span className="spacer" />
          {current.images_path && (
            <code className="muted small mono">{current.images_path}</code>
          )}
        </div>
        {images.length === 0 ? (
          <p className="muted small" style={{ marginTop: 8 }}>
            {drivePhase === 'completed'
              ? 'No images were found in the resolved game-rules folder.'
              : 'Click "Download Images" to fetch screenshots from Google Drive, or drop your own into the run\'s images folder and click "Scan Local Folder".'}
          </p>
        ) : (
          <div className="image-grid" style={{ marginTop: 12 }}>
            {images.map((img) => {
              const entry = imageLabels?.entries[img.name];
              const labels = entry?.labels ?? [];
              return (
                <button
                  key={img.path}
                  className="image-thumb"
                  onClick={() => setZoom(img)}
                  title={img.name}
                  style={{ position: 'relative' }}
                >
                  <img src={img.url} alt={img.name} loading="lazy" />
                  <div className="image-thumb-name">{img.name}</div>
                  {labels.length > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 3,
                        padding: '4px 6px 6px',
                        justifyContent: 'flex-start',
                      }}
                    >
                      {labels.map((l) => {
                        const def = labelDefByKey.get(l.toLowerCase());
                        return (
                          <span
                            key={l}
                            className="badge small"
                            title={def?.definition ?? l}
                            style={{
                              fontFamily: 'monospace',
                              fontSize: 10,
                              background: 'rgba(99, 102, 241, 0.12)',
                              border: '1px solid rgba(99, 102, 241, 0.4)',
                            }}
                          >
                            {l}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="card">
        <div className="row">
          <strong>Vision Analysis (GPT-4o)</strong>
          <span className="spacer" />
          {analysisExists && (
            <span className="badge good">analysis_log.txt cached</span>
          )}
        </div>
        {analysisExists && analysisContent ? (
          <div className="markdown-content" style={{ marginTop: 12 }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{analysisContent}</ReactMarkdown>
          </div>
        ) : (
          <p className="muted small" style={{ marginTop: 8 }}>
            {drivePhase === 'completed'
              ? 'Click "Analyze Images" to generate the vision summary used by Phase 7.'
              : 'Download the screenshots first - the analyzer reads them off disk.'}
          </p>
        )}
      </div>

      {gallery && <ImagesGalleryDialog runId={runId} onClose={() => setGallery(false)} />}

      {zoom && (
        <div className="modal-backdrop image-zoom-backdrop" onClick={() => setZoom(null)}>
          <div className="image-zoom" onClick={(e) => e.stopPropagation()}>
            <div className="row" style={{ marginBottom: 8 }}>
              <strong>{zoom.name}</strong>
              <span className="spacer" />
              <button className="btn btn-ghost" onClick={() => setZoom(null)}>Close</button>
            </div>
            <img src={zoom.url} alt={zoom.name} />
          </div>
        </div>
      )}
    </div>
  );
}
